/* ===== HOUSE OF NADA — Theme JS v3.1 =====
   Fixes: iOS Safari scroll-lock, backdrop-filter perf,
          section:load memory leak, sticky header.
   ============================================ */
'use strict';

/* ██████ iOS-SAFE SCROLL LOCK ████████████████████████████ */
/**
 * Prevents document.body.style.overflow='hidden' from resetting
 * scroll position on iOS Safari. Uses position:fixed approach.
 */
const ScrollLock = {
  _scrollY: 0,
  _locked: false,
  lock() {
    if (this._locked) return;
    this._locked = true;
    this._scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  },
  unlock() {
    if (!this._locked) return;
    this._locked = false;
    const scrollY = this._scrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, scrollY);
  }
};

/* ██████ CART DRAWER ████████████████████████████████████████ */
class CartDrawer {
  constructor() {
    this.drawer     = document.getElementById('cart-drawer');
    this.backdrop   = document.getElementById('cart-drawer-backdrop');
    this.lineItems  = document.getElementById('cart-line-items');
    this.subtotalEl = document.getElementById('cart-drawer-subtotal');
    this.emptyEl    = document.getElementById('cart-empty');
    this.footerEl   = document.getElementById('cart-drawer-footer');
    this.countEl    = document.getElementById('cart-drawer-count');
    this.badgeEls   = document.querySelectorAll('[data-cart-count]');

    // Open triggers
    document.querySelectorAll('[data-cart-open]').forEach(btn =>
      btn.addEventListener('click', () => this.open())
    );

    // Close triggers
    this.backdrop?.addEventListener('click', () => this.close());
    document.getElementById('cart-drawer-close')?.addEventListener('click', () => this.close());
    document.getElementById('cart-continue-btn')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    // Cart events
    document.addEventListener('cart:add',    () => this.fetchAndRender());
    document.addEventListener('cart:update', () => this.fetchAndRender());
  }

  open() {
    this.fetchAndRender();
    this.drawer?.classList.add('is-open');
    this.drawer?.setAttribute('aria-hidden', 'false');
    this.backdrop?.classList.add('is-open');
    ScrollLock.lock();
  }

  close() {
    this.drawer?.classList.remove('is-open');
    this.drawer?.setAttribute('aria-hidden', 'true');
    this.backdrop?.classList.remove('is-open');
    ScrollLock.unlock();
  }

  async fetchAndRender() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) {
      console.error('[CartDrawer] fetch error', e);
    }
  }

  render(cart) {
    // Update all cart count badges
    const count = cart.item_count;
    this.badgeEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
    if (this.countEl) this.countEl.textContent = count;

    if (!this.lineItems) return;

    if (count === 0) {
      this.emptyEl?.removeAttribute('hidden');
      this.footerEl?.setAttribute('hidden', '');
      this.lineItems.innerHTML = '';
      return;
    }

    this.emptyEl?.setAttribute('hidden', '');
    this.footerEl?.removeAttribute('hidden');

    this.lineItems.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item-img">
          <a href="${item.url}"><img src="${item.image}" alt="${this._esc(item.product_title)}" loading="lazy" width="80" height="96"></a>
        </div>
        <div class="cart-item-info">
          <div class="cart-item-name">${this._esc(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item-variant">${this._esc(item.variant_title)}</div>` : ''}
          <div class="cart-item-price">${this.money(item.final_line_price)}</div>
          <div class="cart-item-controls">
            <div class="cart-item-qty">
              <button class="qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button class="qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
            </div>
            <button class="cart-item-remove" data-key="${item.key}" aria-label="Remove ${this._esc(item.product_title)}">Remove</button>
          </div>
        </div>
      </div>`).join('');

    if (this.subtotalEl) this.subtotalEl.textContent = this.money(cart.total_price);

    // Qty buttons
    this.lineItems.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key  = btn.dataset.key;
        const curr = parseInt(btn.dataset.qty) || 1;
        const next = btn.dataset.action === 'increase' ? curr + 1 : Math.max(0, curr - 1);
        await this.changeQty(key, next);
      });
    });

    // Remove buttons
    this.lineItems.querySelectorAll('.cart-item-remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        await this.changeQty(btn.dataset.key, 0);
      });
    });
  }

  async changeQty(key, qty) {
    try {
      const res  = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: key, quantity: qty })
      });
      const cart = await res.json();
      this.render(cart);
      document.dispatchEvent(new CustomEvent('cart:update'));
    } catch (e) {
      console.error('[CartDrawer] update error', e);
    }
  }

  money(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  _esc(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
  }
}

/* ██████ ADD TO CART ████████████████████████████████████████ */
class AddToCart {
  constructor() {
    // Main product form(s)
    document.querySelectorAll('[data-atc-form]').forEach(form => {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const btn = form.querySelector('[data-atc-btn]');
        if (!btn || btn.disabled) return;
        const orig = btn.innerHTML;
        btn.innerHTML = '<span class="spinner"></span> Adding…';
        btn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { Accept: 'application/javascript' },
            body:    new FormData(form)
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Failed to add to cart');
          }
          document.dispatchEvent(new CustomEvent('cart:add'));
          window.draperCart?.open();
          showToast('Added to your bag! 🎉');
        } catch (err) {
          showToast(err.message || 'Error — please try again');
        } finally {
          btn.innerHTML = orig;
          btn.disabled  = false;
        }
      });
    });

    
      // Quantity selector on product cards
      document.addEventListener('click', e => {
        const minusBtn = e.target.closest('.pc-qty-minus');
        const plusBtn = e.target.closest('.pc-qty-plus');
        
        if (minusBtn || plusBtn) {
          e.preventDefault();
          e.stopPropagation();
          const container = (minusBtn || plusBtn).closest('.pc-qty-selector');
          if (!container) return;
          const input = container.querySelector('.pc-qty-input');
          if (!input) return;
          
          let val = parseInt(input.value) || 1;
          if (minusBtn && val > 1) {
            val--;
          } else if (plusBtn) {
            val++;
          }
          input.value = val;
        }
      });
      
      // Direct add-to-cart for single-variant products with flying animation
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-direct-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      
      const variantId = btn.dataset.directAdd;
      if (!variantId || btn.disabled) return;
      
      // Get quantity from the local input
      const card = btn.closest('.product-card');
      const qtyInput = card ? card.querySelector('.pc-qty-input') : null;
      const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
      
      btn.disabled = true;
      const origHtml = btn.innerHTML;
      btn.innerHTML = '<span>Adding...</span>';
      
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: variantId, quantity: quantity })
        });
        if (!res.ok) throw new Error('Failed to add');
        
        // Trigger cart drawer refresh & open drawer
        if (window.draperCart) {
          await window.draperCart.fetchAndRender();
          window.draperCart.open();
        }
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        btn.innerHTML = origHtml;
        btn.disabled = false;
      }
    });
  }

  flyToCart(sourceEl) {
    const cartIcon = document.querySelector('.header-cart-icon') || document.querySelector('[data-cart-open]');
    if (!cartIcon || !sourceEl) return;
    
    const srcRect = sourceEl.getBoundingClientRect();
    const dstRect = cartIcon.getBoundingClientRect();
    
    const flyer = document.createElement('div');
    flyer.className = 'cart-flyer-orb';
    document.body.appendChild(flyer);
    
    const startX = srcRect.left + srcRect.width / 2;
    const startY = srcRect.top + srcRect.height / 2;
    const endX = dstRect.left + dstRect.width / 2;
    const endY = dstRect.top + dstRect.height / 2;
    
    flyer.style.left = `${startX}px`;
    flyer.style.top = `${startY}px`;
    flyer.style.transform = `translate(-50%, -50%)`;
    
    // Animate
    requestAnimationFrame(() => {
      flyer.style.transition = 'top 0.5s cubic-bezier(0.4, 0, 1, 1), left 0.5s cubic-bezier(0, 0, 0.2, 1), opacity 0.5s ease-out';
      flyer.style.left = `${endX}px`;
      flyer.style.top = `${endY}px`;
      flyer.style.opacity = '0.5';
      
      setTimeout(() => {
        flyer.remove();
        cartIcon.classList.add('cart-icon-pulse');
        setTimeout(() => cartIcon.classList.remove('cart-icon-pulse'), 400);
      }, 500);
    });
  }
}

/* ██████ MEGA MENU ██████████████████████████████████████████ */
class MegaMenu {
  constructor() {
    const backdrop = document.getElementById('mega-menu-backdrop');
    let activePanel = null;
    let timer;

    const openPanel = (panel, trigger) => {
      clearTimeout(timer);
      // Close any other open panel first
      if (activePanel && activePanel !== panel) {
        activePanel.classList.remove('is-open');
        document.querySelectorAll('.mega-trigger').forEach(t => t.setAttribute('aria-expanded', 'false'));
      }
      panel.classList.add('is-open');
      backdrop?.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      activePanel = panel;
    };

    const closeAll = () => {
      timer = setTimeout(() => {
        document.querySelectorAll('.mega-menu[data-mega-panel]').forEach(p => p.classList.remove('is-open'));
        backdrop?.classList.remove('is-open');
        document.querySelectorAll('.mega-trigger').forEach(t => t.setAttribute('aria-expanded', 'false'));
        activePanel = null;
      }, 130);
    };

    // Wire each trigger to its panel
    document.querySelectorAll('.mega-trigger[data-mega-trigger]').forEach(trigger => {
      const handle = trigger.dataset.megaTrigger;
      const panel  = document.querySelector(`.mega-menu[data-mega-panel="${handle}"]`);
      if (!panel) return;

      trigger.addEventListener('mouseenter', () => openPanel(panel, trigger));
      trigger.addEventListener('mouseleave', closeAll);
      trigger.addEventListener('focus',      () => openPanel(panel, trigger));
      trigger.addEventListener('click',      () => {
        panel.classList.contains('is-open') ? closeAll() : openPanel(panel, trigger);
      });

      panel.addEventListener('mouseenter', () => clearTimeout(timer));
      panel.addEventListener('mouseleave', closeAll);
    });

    backdrop?.addEventListener('click', closeAll);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeAll(); });
  }
}

/* ██████ MOBILE MENU ████████████████████████████████████████ */
class MobileMenu {
  constructor() {
    const toggle   = document.getElementById('mobile-menu-toggle');
    const menu     = document.getElementById('mobile-menu');
    const closeBtn = document.getElementById('mobile-menu-close');
    const backdrop = document.getElementById('mobile-menu-backdrop');
    if (!toggle || !menu) return;

    const open  = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      backdrop?.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      ScrollLock.lock();
    };
    const close = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      backdrop?.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      ScrollLock.unlock();
    };

    toggle.addEventListener('click', () => menu.classList.contains('is-open') ? close() : open());
    closeBtn?.addEventListener('click', close);
    backdrop?.addEventListener('click', close);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) close();
    });

    // Accordions
    menu.querySelectorAll('.mobile-menu-accordion-trigger').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const parent = this.closest('[data-accordion]');
        if (parent.classList.contains('is-open')) {
          parent.classList.remove('is-open');
          this.setAttribute('aria-expanded', 'false');
        } else {
          parent.classList.add('is-open');
          this.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }
}

/* ██████ SEARCH DROPDOWN ██████████████████████████████████ */
class SearchOverlay {
  constructor() {
    this.overlay  = document.getElementById('search-overlay');
    this.input    = document.getElementById('search-input');
    this.clearBtn = document.getElementById('search-clear');
    this.closeBtn = document.getElementById('search-close');
    this.defaultPanel  = document.getElementById('search-default');
    this.resultsPanel  = document.getElementById('search-results');
    this.resultsInner  = document.getElementById('search-results-inner');
    this.loadingPanel  = document.getElementById('search-loading');
    this.openBtn       = document.getElementById('search-toggle');

    if (!this.overlay || !this.input) return;

    this._debounceTimer = null;
    this._lastQuery = '';
    this._isOpen = false;

    this._bindEvents();
  }

  _bindEvents() {
    // Open
    this.openBtn?.addEventListener('click', () => this.open());

    // Close
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.querySelectorAll('[data-search-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen) this.close();
    });

    // Input — debounced predictive search
    this.input?.addEventListener('input', () => {
      const val = this.input.value.trim();

      // Toggle clear button
      if (this.clearBtn) {
        this.clearBtn.style.display = val.length > 0 ? 'flex' : 'none';
      }

      // Debounce
      clearTimeout(this._debounceTimer);
      if (val.length < 2) {
        this._showDefault();
        return;
      }
      this._debounceTimer = setTimeout(() => this._search(val), 300);
    });

    // Clear button
    this.clearBtn?.addEventListener('click', () => {
      this.input.value = '';
      this.clearBtn.style.display = 'none';
      this._showDefault();
      this.input.focus();
    });

    // Trending tags — click to search
    this.overlay?.querySelectorAll('[data-search-tag]').forEach(tag => {
      tag.addEventListener('click', (e) => {
        e.preventDefault();
        const term = tag.dataset.searchTag;
        this.input.value = term;
        if (this.clearBtn) this.clearBtn.style.display = 'flex';
        this._search(term);
      });
    });
  }

  open() {
    if (this._isOpen) return;
    this._isOpen = true;
    this._previousFocus = document.activeElement;
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    ScrollLock.lock();
    // Focus input after animation
    setTimeout(() => this.input?.focus(), 350);
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    ScrollLock.unlock();
    // Reset state
    clearTimeout(this._debounceTimer);
    if (this._previousFocus) {
      this._previousFocus.focus();
      this._previousFocus = null;
    }
  }

  _showDefault() {
    if (this.defaultPanel) this.defaultPanel.style.display = '';
    if (this.resultsPanel) this.resultsPanel.style.display = 'none';
    if (this.loadingPanel) this.loadingPanel.style.display = 'none';
  }

  _showLoading() {
    if (this.defaultPanel) this.defaultPanel.style.display = 'none';
    if (this.resultsPanel) this.resultsPanel.style.display = 'none';
    if (this.loadingPanel) this.loadingPanel.style.display = '';
  }

  _showResults() {
    if (this.defaultPanel) this.defaultPanel.style.display = 'none';
    if (this.loadingPanel) this.loadingPanel.style.display = 'none';
    if (this.resultsPanel) this.resultsPanel.style.display = '';
  }

  async _search(query) {
    if (query === this._lastQuery) return;
    this._lastQuery = query;

    this._showLoading();

    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`
      );
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      const products = data?.resources?.results?.products || [];

      this._renderResults(products, query);
    } catch (err) {
      console.warn('[SearchDropdown] Predictive search error:', err);
      this._resultsInnerHTML(`
        <div class="search-results__empty">
          <div class="search-results__empty-title">Something went wrong</div>
          <div class="search-results__empty-text">Please try again or press Enter to search.</div>
        </div>
      `);
      this._showResults();
    }
  }

  _renderResults(products, query) {
    if (products.length === 0) {
      this._resultsInnerHTML(`
        <div class="search-results__empty">
          <svg class="search-results__empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <div class="search-results__empty-title">No results for "${this._escapeHtml(query)}"</div>
          <div class="search-results__empty-text">Try a different search term or browse our collections.</div>
        </div>
      `);
      this._showResults();
      return;
    }

    const encodedQ = encodeURIComponent(query);
    let html = `
      <div class="search-results__header">
        <span class="search-results__count">${products.length} result${products.length > 1 ? 's' : ''}</span>
        <a href="/search?q=${encodedQ}&type=product" class="search-results__view-all">View all results →</a>
      </div>
      <div class="search-results__grid">
    `;

    products.forEach((p, idx) => {
      const img = p.image || p.featured_image?.url || '';
      const price = this._formatMoney(p.price);
      const comparePrice = p.compare_at_price_max && p.compare_at_price_max > p.price
        ? `<span class="search-result__price--compare">${this._formatMoney(p.compare_at_price_max)}</span>`
        : '';
      const delay = 0.05 + (idx * 0.04);

      html += `
        <a href="${p.url}" class="search-result__card" style="animation: searchFadeUp 0.4s cubic-bezier(0.16,1,0.3,1) ${delay}s both;">
          <div class="search-result__image">
            ${img ? `<img src="${img}" alt="${this._escapeHtml(p.title)}" loading="lazy" width="200" height="267">` : ''}
          </div>
          <div class="search-result__info">
            <span class="search-result__vendor">${this._escapeHtml(p.vendor || '')}</span>
            <span class="search-result__title">${this._escapeHtml(p.title)}</span>
            <span class="search-result__price">${price}${comparePrice}</span>
          </div>
        </a>
      `;
    });

    html += `</div>`;

    this._resultsInnerHTML(html);
    this._showResults();
  }

  _resultsInnerHTML(html) {
    if (this.resultsInner) this.resultsInner.innerHTML = html;
  }

  _formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    // Shopify suggest API returns price as string in cents format
    const amount = (parseFloat(cents) / 100).toFixed(2);
    return `Rs. ${amount}`;
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
}

/* ██████ PRODUCT GALLERY ████████████████████████████████████ */
class ProductGallery {
  constructor() {
    document.querySelectorAll('.product-thumbs-strip').forEach(strip => {
      const mainImg = document.getElementById('gallery-main-img');
      const thumbs  = strip.querySelectorAll('.product-thumb');

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          thumbs.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          const fullSrc = thumb.dataset.fullSrc;
          if (mainImg && fullSrc) {
            mainImg.src = fullSrc;
          }
        });
      });
    });
  }
}

/* ██████ PRODUCT TABS ███████████████████████████████████████ */
class ProductTabs {
  constructor() {
    // Handled inline in main-product.liquid (scoped to section),
    // but also support global use
    document.querySelectorAll('.product-tab-nav').forEach(nav => {
      nav.querySelectorAll('.tab-btn').forEach(tab => {
        tab.addEventListener('click', () => {
          const panelId = tab.dataset.tab;
          nav.querySelectorAll('.tab-btn').forEach(t => {
            t.classList.remove('active');
            t.setAttribute('aria-selected', 'false');
          });
          document.querySelectorAll('.tab-panel').forEach(p => { p.hidden = true; });
          tab.classList.add('active');
          tab.setAttribute('aria-selected', 'true');
          const panel = document.getElementById(panelId);
          if (panel) panel.hidden = false;
        });
      });
    });
  }
}

/* ██████ QTY SELECTOR (scoped per .qty-selector container) ██ */
class QtySelector {
  constructor() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('.qty-selector .qty-btn');
      if (!btn || btn.dataset.key) return; // Skip cart-drawer (handled by CartDrawer)
      const container = btn.closest('.qty-selector');
      const input = container?.querySelector('.qty-input');
      if (!input) return;
      let val = parseInt(input.value) || 1;
      val = btn.dataset.action === 'increase' ? val + 1 : Math.max(1, val - 1);
      input.value = val;
    });
  }
}

/* ██████ SCROLL REVEAL ██████████████████████████████████████ */
class ScrollReveal {
  constructor() {
    const els = document.querySelectorAll('.fade-in-up');
    if (!els.length) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -10px 0px' });
    els.forEach(el => obs.observe(el));
  }
}

/* ██████ ANNOUNCEMENT MARQUEE ██████████████████████████████ */
class Marquee {
  constructor() {
    document.querySelectorAll('.marquee-inner').forEach(inner => {
      // Only clone once
      if (inner.dataset.cloned) return;
      const clone = inner.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.cloned = 'true';
      inner.parentElement.appendChild(clone);
      inner.dataset.cloned = 'true';
    });
  }
}

/* ██████ TOAST ██████████████████████████████████████████████ */
function showToast(msg, duration = 3000) {
  let toast = document.getElementById('drape-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'drape-toast';
    toast.className = 'toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.classList.add('show');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('show'), duration);
}

/* ██████ QUICK VIEW ██████████████████████████████████████████ */
class QuickView {
  constructor() {
    this.modal = document.getElementById('quick-view-modal');
    this.overlay = document.getElementById('qv-overlay'); // if overlay is outside container
    this.content = document.getElementById('qv-content');
    if (!this.modal) return;

    // Delegate clicks for quick add buttons
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-quick-add]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        this.open(btn.dataset.productHandle);
      }
    }, true);

    // Close logic
    this.modal.addEventListener('click', e => {
      if (e.target.classList.contains('modal-overlay') || e.target.closest('.modal-close')) {
        this.close();
      }
    });
  }

  async open(handle) {
    if (!handle) return;
    ScrollLock.lock();
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.content.innerHTML = '<div style="display:flex;justify-content:center;padding:100px;"><span class="spinner"></span></div>';

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();
      this.render(product);
    } catch (e) {
      this.content.innerHTML = `<div style="padding:40px;text-align:center;">Error loading product details.</div>`;
    }
  }

  close() {
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    ScrollLock.unlock();
  }

  render(product) {
    let selectedOptions = {};
    product.options.forEach(opt => selectedOptions[opt.name] = opt.values[0]);

    const findVariant = () => {
      return product.variants.find(v => {
        return product.options.every((opt, idx) => v.options[idx] === selectedOptions[opt.name]);
      });
    };

    const updateUI = () => {
      const variant = findVariant();
      const atcBtn = this.content.querySelector('.qv-btn-atc');
      const priceEl = this.content.querySelector('.qv-price');
      const moreBtn = this.content.querySelector('.qv-details-link');
      const slider = this.content.querySelector('.qv-media-slider');

      if (variant) {
        priceEl.textContent = '₹' + (variant.price / 100).toLocaleString('en-IN');
        if (atcBtn) {
          atcBtn.disabled = !variant.available;
          atcBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }
        if (moreBtn) moreBtn.href = `/products/${product.handle}?variant=${variant.id}`;
        
        if (variant.featured_image) {
          const targetSrc = (variant.featured_image.src || variant.featured_image.url || '').split('?')[0];
          if (targetSrc) {
            const allSlides = this.content.querySelectorAll('.qv-slide img');
            for (let i = 0; i < allSlides.length; i++) {
              if (allSlides[i].src.includes(targetSrc)) {
                allSlides[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
                break;
              }
            }
          }
        }
      }
    };

    const money = (cents) => '₹' + (cents / 100).toLocaleString('en-IN');

    this.content.innerHTML = `
      <div class="qv-grid">
        <div class="qv-media">
          <div class="qv-media-slider">
            ${product.images.map(src => `
              <div class="qv-slide">
                <img src="${src}" loading="lazy" alt="${product.title}">
              </div>
            `).join('')}
          </div>
        </div>
        <div class="qv-info">
          <h2 class="qv-title">${product.title}</h2>
          <div class="qv-price">${money(product.variants[0].price)}</div>
          
          <div class="qv-options">
            ${product.options.map(opt => `
              <div class="qv-option-group" data-option-name="${opt.name}">
                <label class="qv-option-label">${opt.name}</label>
                <div class="qv-option-values">
                  ${opt.values.map(val => {
                    const isColor = /color|colour/i.test(opt.name);
                    if (isColor) {
                      return `
                        <button class="qv-color-swatch ${val === selectedOptions[opt.name] ? 'is-active' : ''}" 
                          data-value="${val}" 
                          title="${val}">
                          <div class="swatch-inner" style="background-color: ${val.toLowerCase().replace(/[^a-z]/g, '')}"></div>
                        </button>
                      `;
                    } else {
                      return `
                        <button class="qv-option-chip ${val === selectedOptions[opt.name] ? 'is-active' : ''}" 
                          data-value="${val}">
                          ${val}
                        </button>
                      `;
                    }
                  }).join('')}
                </div>
              </div>
            `).join('')}
          </div>

          <div class="qv-btns">
            <button class="qv-btn qv-btn-atc">Add to Cart</button>
            <button class="qv-btn qv-btn-buy">Buy Now</button>
            <a href="/products/${product.handle}" class="qv-details-link">See More Details</a>
          </div>
        </div>
      </div>
    `;

    // Attach listeners to swatches and chips
    this.content.querySelectorAll('.qv-color-swatch, .qv-option-chip').forEach(el => {
      el.addEventListener('click', () => {
        const group = el.closest('.qv-option-group');
        const optName = group.dataset.optionName;
        selectedOptions[optName] = el.dataset.value;
        
        group.querySelectorAll('.qv-color-swatch, .qv-option-chip').forEach(s => s.classList.remove('is-active'));
        el.classList.add('is-active');
        updateUI();
      });
    });

    const atcBtn = this.content.querySelector('.qv-btn-atc');
    const buyBtn = this.content.querySelector('.qv-btn-buy');

    if (atcBtn) {
      atcBtn.onclick = async () => {
        const variant = findVariant();
        if (!variant) return;
        const orig = atcBtn.textContent;
        atcBtn.textContent = 'Adding...';
        atcBtn.disabled = true;
        try {
          const cartRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
          });
          if (!cartRes.ok) throw new Error();
          this.close();
          document.dispatchEvent(new CustomEvent('cart:add'));
          window.draperCart?.open();
        } catch (e) {
          showToast('Error adding to cart');
        } finally {
          atcBtn.textContent = orig;
          atcBtn.disabled = false;
        }
      };
    }

    if (buyBtn) {
      buyBtn.onclick = () => {
        const variant = findVariant();
        if (variant) window.location.href = `/cart/${variant.id}:1`;
      };
    }

    updateUI();
  }
}

/* ██████ STICKY HEADER ██████████████████████████████████████ */
/* Sticky positioning is now handled by pure CSS on #shopify-section-* wrapper.
   This JS class only adds/removes the .scrolled class for visual effects (shadow). */
class StickyHeader {
  constructor() {
    this.header = document.querySelector('.site-header');
    if (!this.header) return;

    if (window._stickyHeaderInit) return;
    window._stickyHeaderInit = true;

    this.ticking = false;
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          var headerEl = document.querySelector('.site-header');
          if (headerEl) {
            headerEl.classList.toggle('scrolled', window.scrollY > 20);
          }
          this.ticking = false;
        });
        this.ticking = true;
      }
    }, { passive: true });
  }
}

/* ██████ GLOBAL MODAL & NAVIGATION CLEANUP ████████████████ */
function closeAllModals() {
  const modals = [
    document.getElementById('size-guide-modal'),
    document.getElementById('quick-view-modal'),
    document.getElementById('cart-drawer'),
    document.getElementById('mobile-menu'),
    document.getElementById('search-overlay')
  ];

  modals.forEach(modal => {
    if (modal) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    }
  });

  const overlays = document.querySelectorAll('.mobile-drawer-overlay, .modal-overlay, .search-overlay-bg');
  overlays.forEach(overlay => overlay.classList.remove('is-open'));

  if (window.ScrollLock) window.ScrollLock.unlock();
}

// Reset all modal states on browser back/forward cache (pageshow) & history popstate
window.addEventListener('pageshow', closeAllModals);
window.addEventListener('popstate', closeAllModals);

/* ██████ SIZE GUIDE MODAL ████████████████████████████████████ */
class SizeGuide {
  constructor() {
    this.modal = document.getElementById('size-guide-modal');
    if (!this.modal) return;

    this.dynamicImg = this.modal.querySelector('[data-dynamic-img="product"]');
    this.isOpen = false;
    this.init();
  }

  init() {
    // Ensure modal starts completely closed
    this.close();

    // Delegate clicks for size guide trigger buttons
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-open-size-guide]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        const productImg = btn.dataset.productImage;
        this.open(productImg);
      }
    });

    this.closeBtns = this.modal.querySelectorAll('[data-close-size-guide]');
    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  open(productImgUrl) {
    if (productImgUrl && this.dynamicImg) {
      if (this.dynamicImg.tagName === 'IMG') {
        this.dynamicImg.src = productImgUrl;
      } else {
        const img = document.createElement('img');
        img.src = productImgUrl;
        img.className = 'sg-img-product';
        img.dataset.dynamicImg = 'product';
        this.dynamicImg.replaceWith(img);
        this.dynamicImg = img;
      }
    }
    
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.isOpen = true;
    ScrollLock.lock();
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.isOpen = false;
    ScrollLock.unlock();
  }
}

/* ██████ INIT ███████████████████████████████████████████████ */
function initTheme() {
  if (window._themeInitialized) return;
  window._themeInitialized = true;

  // Always force close lingering modals on load
  closeAllModals();

  window.draperCart = new CartDrawer();
  new AddToCart();
  new MegaMenu();
  new MobileMenu();
  new SearchOverlay();
  new ProductGallery();
  new ProductTabs();
  new QtySelector();
  new ScrollReveal();
  new Marquee();
  new StickyHeader();
  window.draperSizeGuide = new SizeGuide();
  window.draperQuickView = new QuickView();

  initProductCardSliders();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTheme);
} else {
  initTheme();
}

/* ██████ PRODUCT CARD SLIDER ████████████████████████████████ */
/**
 * Global high-performance image slider for product cards.
 * Handles arrow buttons, dot indicators, and touch swipe.
 */
// Global explicit product card action handlers
window.pcSlideCard = function(btn, dir) {
  if (!btn) return;
  const card = btn.closest('[data-product-card]');
  if (!card) return;

  const inner = card.querySelector('[data-pc-slider-inner]');
  const slider = card.querySelector('[data-pc-slider]');
  const slides = card.querySelectorAll('[data-pc-slide]');
  const dots = card.querySelectorAll('[data-pc-dot]');
  if ((!inner && !slider) || slides.length < 2) return;

  let cur = parseInt(card.dataset.pcIndex || '0');
  cur = (cur + dir + slides.length) % slides.length;
  card.dataset.pcIndex = cur;

  if (inner) {
    inner.style.transform = `translateX(-${cur * 100}%)`;
  } else if (slider) {
    const slideWidth = slider.clientWidth || slider.offsetWidth;
    slider.scrollTo({ left: cur * slideWidth, behavior: 'smooth' });
  }

  dots.forEach((dot, i) => {
    dot.classList.toggle('is-active', i === cur);
  });
};

window.pcSlideTo = function(el, targetIndex) {
  if (!el) return;
  const card = el.closest('[data-product-card]');
  if (!card) return;

  const inner = card.querySelector('[data-pc-slider-inner]');
  const slider = card.querySelector('[data-pc-slider]');
  const slides = card.querySelectorAll('[data-pc-slide]');
  const dots = card.querySelectorAll('[data-pc-dot]');
  if (targetIndex < 0 || targetIndex >= slides.length) return;

  card.dataset.pcIndex = targetIndex;

  if (inner) {
    inner.style.transform = `translateX(-${targetIndex * 100}%)`;
  } else if (slider) {
    const slideWidth = slider.clientWidth || slider.offsetWidth;
    slider.scrollTo({ left: targetIndex * slideWidth, behavior: 'smooth' });
  }

  dots.forEach((dot, i) => {
    dot.classList.toggle('is-active', i === targetIndex);
  });
};

window.pcAddToCart = async function(btn) {
  if (!btn) return;
  const variantId = btn.dataset.directAdd;
  if (!variantId || btn.disabled) return;

  btn.disabled = true;
  const origHtml = btn.innerHTML;
  btn.innerHTML = '<span class="plus-icon">✓</span> Adding...';

  try {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    });

    if (!res.ok) throw new Error('Failed to add to cart');

    if (!window.draperCart) {
      window.draperCart = new CartDrawer();
    }
    await window.draperCart.fetchAndRender();
    window.draperCart.open();
  } catch (err) {
    console.error('Add to cart error:', err);
    alert('Item could not be added to cart. Please try again.');
  } finally {
    setTimeout(() => {
      btn.innerHTML = origHtml;
      btn.disabled = false;
    }, 600);
  }
};

function initProductCardSliders() {
  document.querySelectorAll('[data-product-card]').forEach(card => {
    if (card._pcSliderInit) return;
    card._pcSliderInit = true;

    const inner = card.querySelector('[data-pc-slider-inner]');
    const slides = card.querySelectorAll('[data-pc-slide]');
    const dots = card.querySelectorAll('[data-pc-dot]');
    if (!inner || slides.length < 2) return;

    let currentIndex = parseInt(card.dataset.pcIndex || '0');
    const updateSlider = (index) => {
      currentIndex = (index + slides.length) % slides.length;
      inner.style.transform = `translateX(-${currentIndex * 100}%)`;
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === currentIndex);
      });
      card.dataset.pcIndex = currentIndex;
    };
    updateSlider(currentIndex);
  });
}

// Re-init on Shopify Section Load (Theme Editor support)
document.addEventListener('shopify:section:load', (e) => {
  new StickyHeader();
  new ScrollReveal();
  new Marquee();
  initProductCardSliders();
  window.draperQuickView = new QuickView();
});
